export type xObject = Record<string, any>;

/**
 * This function takes a list of property names and returns a function that takes an object
 * and returns true if all the listed properties present in either objects are equal.
 * @param props A list of property names.
 * @returns A function that takes two objects and returns true if all the listed properties
 * present in either objects are equal. Useful for `Array.prototype.sort()` and
 * `distinctUntilChanged()`.
 */
export function objectPropsEqual<T extends Record<string, unknown>>(...props: (keyof T)[]) {
  return (a: T, b: T) => props.every((prop) => a[prop] === b[prop]);
}

/**
 * Copies properties from one object to another selectively.
 * @param propertyPaths A list of property names or paths to copy from the source to the destination.
 * @param destination An object to copy the properties to.
 * @param source An object to copy the properties from.
 * @param formatter An optional function to format the value before copying.
 */
export function copyProps<T,K>(propertyPaths: string[], destination: T, source: K, formatter?: (value: unknown) => unknown) {
	propertyPaths.forEach(prop => {
		const props = prop.split('.');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let src: any = source;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let dest: any = destination;
		for (let i = 0; i < props.length; i++) {
			const p = props[i];
			const atEnd = i >= props.length - 1;
			src = src[p];
			if (typeof src === 'undefined') return; // on to the next prop
			if (!atEnd) {
				dest = dest[p] ?? (dest[p] = {});
			} else {
				dest[p] = formatter ? formatter(src) : src;
			}
		}
	});
	return destination;
}

/**
 * Set a list of properties on an object.
 * @param destination An object the properties will be set on
 * @param values An array of path (dot notation) value pairs to set on the destination object.
 * @returns The destination object with the new properties set.
 */
export function setProps<T>(destination: T, values: [string, unknown][]) {
	values.forEach(([key, val]) => {
		const props = key.split('.');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let obj = destination as any;
		const end = props.length - 1;
		for (let i = 0; i < end; i++) {
			const prop = props[i];
			if (typeof obj[prop] === 'undefined') obj[prop] = {};
			obj = obj[prop];
		}
		obj[props[end]] = val;
	});
	return destination;
}

/**
 * Deep copies properties from one object to another.
 * TODO: copy date objects
 * @param destination Any object or array to merge into
 * @param source Any object oor array of which all propeties/indecies will be recursively copied into destination
 */
export function deepCopy(destination: any, source: any) {
	if (Array.isArray(source)) {
		destination.length = source.length;
		source.forEach((item, index) => deepCopyProp(destination, source, index));
	} else if (typeof source === 'object' && source !== null) {
		Object.keys(source).forEach(key => deepCopyProp(destination, source, key));
	}
	return destination;
}

/**
 * Copies a property from one object or array to another, recursively if necessary.
 * Note: unit test covered in `deepCopy()` tests
 * @param destination Any object that needs `prop` copied into it
 * @param source Any object taht supplies the `prop` to be copied
 * @param prop The array index or object key of the property to be copied
 */
export function deepCopyProp(destination: any, source:any, prop: string | number) {
	const isObj = typeof source[prop] === 'object' && source[prop] !== null;
	if (typeof destination[prop] === 'undefined') {
		if (Array.isArray(source[prop])) {
			destination[prop] = [];
		} else if (isObj) {
			destination[prop] = {};
		}
	}
	const isDestObj = typeof destination[prop] === 'object' && destination[prop] !== null;
	if (isObj) {
		if (!isDestObj) throw new Error(`destination.${prop} is not an object.`);
		deepCopy(destination[prop], source[prop]);
	} else {
		if (isDestObj) throw new Error(`destination.${prop} is an object.`);
		destination[prop] = source[prop];
	}
}

/**
 * Creates a deep clone of the provided source object or array.
 *
 * @template T - The type of the source object or array.
 * @param {T} source - The source object or array to clone.
 * @returns {T} - A deep clone of the source object or array.
 */
export function clone<T = any>(source: T): T {
	if (Array.isArray(source)) {
		return Array.from(source).map(clone) as T;
	}
	if (source instanceof Date) {
		return new Date(source.getTime()) as T;
	}
	if (typeof source === 'object' && source !== null) {
		const destination = {};
		Object.keys(source).forEach(key => deepCopyProp(destination, source, key));
		return destination as T;
	}
	
	return source; // primitive
}

//#############################################################
// ASYNC FUNCTIONS
//#############################################################

export function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function forEachAsync<T>(arr: T[], fn: (item: T, index: number, arr:T[]) => Promise<any>) {
	for (let i = 0; i < arr.length; i++) await fn(arr[i], i, arr);
}

export async function retry<T>(cb: () => Promise<T>, retries = 3, delay = 1000, test?: (res:T) => boolean) {
	let error;
	for (let i = 0; i < retries; i++) {
		try {
			const res = await cb();
			if (!test) return res;
			if (test(res)) return res; else throw new Error('Retry test failed.');
		} catch (err) {
			error = err;
			await sleep(delay);
		}
	}
	throw error;
}
